/**
 * L1 Phase 2 — client-side 401 interceptor.
 *
 * In Next-land the middleware short-circuits unauthenticated requests
 * to /api/* and redirects them to /auth/entry. In Vite-SPA-land the
 * middleware is gone; the Go backend just returns 401 on protected
 * routes, and the client has to spot the 401 and redirect.
 *
 * Strategy: monkey-patch window.fetch once at app init. Any /api/*
 * call that returns 401 triggers a redirect to /auth/entry, preserving
 * the current path as ?from=. Non-/api/* calls and non-401 responses
 * pass through unchanged.
 *
 * Auth-entry calls are exempt (the token-check call itself produces
 * 401 on bad token — we don't want a redirect loop there).
 */
const PROTECTED_API_PREFIX = "/api/";
const AUTH_ENTRY_PATH = "/auth/entry";
const AUTH_CHECK_PATH = "/api/auth/token-check";

let installed = false;

export function installAuthInterceptor(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;

  const original = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url = typeof input === "string"
      ? input
      : input instanceof URL
        ? input.toString()
        : input.url;

    const response = await original(input, init);

    // Only intercept 401s from protected /api routes.
    // The token-check endpoint legitimately returns 401 on a bad
    // token; the entry page handles that locally, so don't redirect.
    if (
      response.status === 401 &&
      url.includes(PROTECTED_API_PREFIX) &&
      !url.includes(AUTH_CHECK_PATH) &&
      window.location.pathname !== AUTH_ENTRY_PATH
    ) {
      const from = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.assign(`${AUTH_ENTRY_PATH}?from=${from}`);
      // Throw so the caller sees a rejection rather than a 401 they
      // might try to handle. The redirect is already in flight; the
      // page will unload before this rejection propagates anywhere
      // user-visible.
      throw new Error("auth-redirect-in-flight");
    }

    return response;
  };
}
